import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  HttpErrorResponse,
} from '@angular/common/http';
import '@angular/common/locales/global/pl';
import { isPlatformBrowser, XhrFactory } from '@angular/common';
import {
  LOCALE_ID,
  NgModule,
  APP_INITIALIZER,
  ErrorHandler,
  PLATFORM_ID,
} from '@angular/core';
import { LayoutModule } from '@angular/cdk/layout';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SwiperModule } from 'swiper/angular';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SliderModule } from 'primeng/slider';
import { environment } from '../environments/environment';
import { allowInputNumberPaste } from './shared/allow-input-number-paste';
import { gtmIdProvider } from './shared/gtm';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TrustedXhrFactory } from './http/trusted-xhr.factory';
import { SentryInterceptor } from './interceptors/sentry.interceptor';
import { BenefitsSliderComponent } from './pages/home/benefits-slider/benefits-slider.component';
import { BenefitsComponent } from './pages/home/benefits/benefits/benefits.component';
import { ContactComponent } from './pages/home/contact/contact.component';
import { CooperationComponent } from './pages/home/cooperation/cooperation.component';
import { DocumentsComponent } from './pages/home/documents/documents.component';
import { AnswerQuestionComponent } from './pages/home/faq/answer-question/answer-question.component';
import { FaqComponent } from './pages/home/faq/faq.component';
import { ForWhoComponent } from './pages/home/for-who/for-who.component';
import { HomeComponent } from './pages/home/home.component';
import { SeoLandingComponent } from './pages/seo-landing/seo-landing.component';
import { NotAGapComponent } from './pages/home/not-a-gap/not-a-gap.component';
import { ValidVehiclesComponent } from './pages/home/valid-vehicles/valid-vehicles.component';
import { WhatIsGapComponent } from './pages/home/what-is-gap/what-is-gap.component';
import { WhenToBuyComponent } from './pages/home/when-to-buy/when-to-buy.component';
import { NavBarWarrantiesComponent } from './pages/nav-bar-warranties/nav-bar-warranties.component';
import { NavBarComponent } from './pages/nav-bar/nav-bar.component';
import { PaymentRedirectComponent } from './pages/payment-redirect/payment-redirect.component';
import { PolicyCreatorInterceptor } from './pages/policy-creator/policy-creator-interceptor';
import { SharedModule } from './pages/shared/shared.module';
import { ApiService } from './services/api.service';
import { MessengerService } from './services/messenger.service';
import { ViewportService } from './services/viewport.service';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { CookieService } from 'ngx-cookie-service';
import { toSentryHttpError } from './utils/sentry-http-error.util';

import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

const sentryErrorHandlerFactory = (platformId: Object): ErrorHandler => {
  if (!isPlatformBrowser(platformId)) {
    return new ErrorHandler();
  }
  return Sentry.createErrorHandler({
    showDialog: false,
    extractor: (error, defaultExtractor) => {
      const extracted = defaultExtractor(error);
      if (extracted instanceof HttpErrorResponse) {
        return toSentryHttpError(extracted);
      }
      if (error instanceof HttpErrorResponse) {
        return toSentryHttpError(error);
      }
      return extracted;
    },
  });
};

const sentryTraceServiceFactory = (router: Router, platformId: Object) => {
  if (!isPlatformBrowser(platformId)) {
    return null;
  }
  return new Sentry.TraceService(router);
};

const sentryProviders = environment.SENTRY_MONITORING
  ? [
      {
        provide: ErrorHandler,
        useFactory: sentryErrorHandlerFactory,
        deps: [PLATFORM_ID],
      },
      {
        provide: Sentry.TraceService,
        useFactory: sentryTraceServiceFactory,
        deps: [Router, PLATFORM_ID],
      },
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [Sentry.TraceService],
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: SentryInterceptor,
        multi: true,
      },
    ]
  : [];


@NgModule({
  declarations: [
    AppComponent,
    NavBarComponent,
    NavBarWarrantiesComponent,
    HomeComponent,
    SeoLandingComponent,
    BenefitsComponent,
    WhatIsGapComponent,
    BenefitsSliderComponent,
    NotAGapComponent,
    ForWhoComponent,
    ValidVehiclesComponent,
    WhenToBuyComponent,
    DocumentsComponent,
    FaqComponent,
    ContactComponent,
    CooperationComponent,
    AnswerQuestionComponent,
    PaymentRedirectComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    LayoutModule,
    CheckboxModule,
    HttpClientModule,
    NgxsModule.forRoot([]),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    SharedModule,
    SliderModule,
    SwiperModule,
    TransferHttpCacheModule,
    TranslocoRootModule,
    AngularSvgIconModule.forRoot(),
    ProgressSpinnerModule,
  ],
  providers: [
    provideClientHydration(),
    CookieService,
    ApiService,
    { provide: LOCALE_ID, useValue: 'pl' },
    { provide: XhrFactory, useClass: TrustedXhrFactory },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PolicyCreatorInterceptor,
      multi: true,
    },
    MessengerService,
    ...sentryProviders,
    ...gtmIdProvider,
    ViewportService,
  ],
  exports: [],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    allowInputNumberPaste();
  }
}
